import {Injectable} from "@angular/core";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {of, Subject} from "rxjs";

interface IrebalPreference {
  preferenceName: string;
  preferenceValue: any;
  version?: number;
  isDeleted: boolean;
  isModified?: boolean;
}

@Injectable({
  providedIn: "root",
})
export class PreferencesService {

  public irebalPreferences: Array<IrebalPreference> = [];
  public gridFilterPreferences: any = {};
  private gridFilterPreferenceNames: Array<string> = [
    'cash-report-filter-pref',
    'allocation-report-filter-pref',
    'auto-rebalance-report-filter-pref',
    'portfolios-filter-pref',
    'assigned-accounts-filter-pref',
    'unassigned-accounts-filter-pref',
    'models-filter-pref',
    'mmc-inventory-filter-pref',
    'security-master-filter-pref',
    'rebalance-results-filter-pref',
    'schedule-rebalances-filter-pref'
  ];
  private gridColumnPreferenceNames: Array<string> = [
    'unassigned-accounts-column-pref',
    'assigned-accounts-column-pref',
    'portfolios-column-pref',
    'models-column-pref',
    'mmc-inventory-column-pref',
    'security-master-column-pref',
    'rebalance-results-column-pref',
    'schedule-rebalances-column-pref',
    'allocation-report-column-pref',
    'cash-report-column-pref',
    'auto-rebalance-report-column-pref'
  ];

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type':  'application/json',
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache',
      'Expires': 'Sat, 01 Jan 2000 00:00:00 GMT',
      'Access-Control-Allow-Origin': location.hostname
    })
  };
  private isSaving: boolean = false;
  public isUiTest: boolean = false;
  public dataSubject = new Subject<any>();
  public data$ = this.dataSubject.asObservable();

  constructor(private http: HttpClient) {
    this.gridFilterPreferenceNames = this.gridFilterPreferenceNames.concat(this.gridColumnPreferenceNames);
  }

  setGridFilterPreference(preferenceName: string, filterModel: any, initialLoadTriggered: boolean) {
    if (initialLoadTriggered)
      return;

    if (!this.isSaving) {
      const timeout = this.isUiTest ? 0 : 10000;
      setTimeout(() => {
        this.isSaving = false;
        this.saveIrebalPreferences();
      }, timeout);
      this.isSaving = true;
    }

    if (this.gridFilterPreferences[preferenceName]) {
      this.gridFilterPreferences[preferenceName].preferenceValue = filterModel;
      this.gridFilterPreferences[preferenceName].isModified = true;
      if (!filterModel) {
        this.gridFilterPreferences[preferenceName].isDeleted = true;
        this.gridFilterPreferences[preferenceName].preferenceValue = {};
        const foundPref = this.irebalPreferences.find((pref: any) => pref.preferenceName === preferenceName);
        if (!foundPref) { // delete if it didn't exist in DB
          delete this.gridFilterPreferences[preferenceName];
        }
      }

    } else {
      if (filterModel) {
        this.gridFilterPreferences[preferenceName] = {
          preferenceName: preferenceName,
          preferenceValue: filterModel,
          isModified: true,
          isDeleted: false
        };
      }
    }
  }

  getGridFilterPreference(preferenceName: string) {
    return this.gridFilterPreferences[preferenceName] ? this.gridFilterPreferences[preferenceName].preferenceValue : null;
  }

  saveIrebalPreferences(preference: IrebalPreference = null) {
    let payload: Array<IrebalPreference> = [];
    if (preference) {
      const foundPref = this.irebalPreferences.find((pref: any) => pref.preferenceName === preference.preferenceName);
      if (!foundPref)
        this.irebalPreferences.push(preference);
      payload.push(preference);
    } else {
      let modifiedPrefs: Array<IrebalPreference> = [];
      for (const key in this.gridFilterPreferences) {
        if (this.gridFilterPreferences[key].isModified) {
          delete this.gridFilterPreferences[key].isModified;
          modifiedPrefs.push(this.gridFilterPreferences[key]);
        }
      }

      if (modifiedPrefs.length) {
        payload = modifiedPrefs;
      }
    }
    if (payload && payload.length)
      this.http.post('/irebalone/user-pref/save-user-preferences', payload, this.httpOptions).subscribe({
        next: (res: any) => {
          if (res && res.data && res.data.irebalResponse) {
            res.data.irebalResponse.forEach((updatedPref: any) => {
              this.irebalPreferences.forEach((prefObject: any) => {
                if (prefObject.preferenceName === updatedPref.preferenceName) {
                  prefObject.preferenceValue = updatedPref.preferenceValue;
                  prefObject.isDeleted = false;
                }
              });
              updatedPref.isModified = false;
              updatedPref.isDeleted = false;
              if (this.gridFilterPreferenceNames.includes(updatedPref.preferenceName))
                this.gridFilterPreferences[updatedPref.preferenceName] = updatedPref;
            });
          }
        }
      });
  }

  getIrebalPreferences() {
    this.http.get('/irebalone/user-pref/user-preferences', this.httpOptions).subscribe({
      next: (res: any) => {
          if (res && res.data && res.data.irebalResponse) {
            this.irebalPreferences = res.data.irebalResponse;
            this.getGridPreferencesFromIrebal();
          }
        },
        complete: () => {
          this.dataSubject.next(true);
        }
    })
  }

  getGridPreferencesFromIrebal() {
    this.irebalPreferences.forEach((prefObject: any) => {
      prefObject.isModified = false;
      delete prefObject.version;
      if (this.gridFilterPreferenceNames.includes(prefObject.preferenceName)) {
        if (prefObject.preferenceValue && JSON.stringify(prefObject.preferenceValue) !== '{}') {
          this.gridFilterPreferences[prefObject.preferenceName] = prefObject;
        }
      }
    });
  }

  getPreferenceByName(prefName: string) {
    let preference: IrebalPreference;
    this.irebalPreferences.forEach((prefObject: any) => {
      if (prefObject.preferenceName === prefName) {
        preference = prefObject;
      }
    });
    return preference;
  }

}
